import { normalizeStaffRole, type StaffRole } from "@/types/auth";
import { createClient } from "@/lib/supabase";
import type { StaffProfile } from "@/core/services/types/team.types";

type SignInRepositoryResult = {
  success: boolean;
  role?: StaffRole;
  error?: string;
};

type UpdatePasswordRepositoryResult = {
  success: boolean;
  error?: string;
};

type InviteStaffRepositoryResult = {
  success: boolean;
  emailSent?: boolean;
  inviteUrl?: string;
  error?: string;
};

type SyncStaffRoleRepositoryResult = {
  success: boolean;
  role?: StaffRole;
  error?: string;
};

type AuthorizedStaffRecord = {
  id: string;
  role: "ADMIN" | "EDITOR";
};

export const findAuthorizedStaffByEmail = async (
  email: string,
): Promise<AuthorizedStaffRecord | null> => {
  const supabase = await createClient();
  const normalizedEmail = email.trim().toLowerCase();

  const { data, error } = await supabase
    .from("team_members")
    .select("id, role")
    .eq("email", normalizedEmail)
    .eq("is_active", true)
    .maybeSingle();

  if (error) {
    console.error("[auth.repository.findAuthorizedStaffByEmail]", error.message);
    return null;
  }

  return data;
};

export const findStaffProfileByEmail = async (
  email: string,
): Promise<StaffProfile | null> => {
  const supabase = await createClient();
  const normalizedEmail = email.trim().toLowerCase();

  const { data, error } = await supabase
    .from("team_members")
    .select("id, first_name, last_name, role")
    .eq("email", normalizedEmail)
    .eq("is_active", true)
    .maybeSingle();

  if (error) {
    console.error("[auth.repository.findStaffProfileByEmail]", error.message);
    return null;
  }

  return data;
};

export const signIn = async (
  email: string,
  password: string,
): Promise<SignInRepositoryResult> => {
  const supabase = await createClient();
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    return {
      success: false,
      error: error.message,
    };
  }

  const role = normalizeStaffRole(
    data.session?.user?.app_metadata?.role as string | undefined,
  );

  return {
    success: true,
    role,
  };
};

export const syncStaffRole = async (): Promise<SyncStaffRoleRepositoryResult> => {
  const supabase = await createClient();
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    return { success: false, error: "Not authenticated." };
  }

  const response = await fetch("/api/auth/sync-staff-role", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
  });

  const body = (await response.json()) as SyncStaffRoleRepositoryResult;

  if (!response.ok || !body.success) {
    return {
      success: false,
      error: body.error ?? "Failed to sync staff role.",
    };
  }

  await supabase.auth.refreshSession();

  return {
    success: true,
    role: normalizeStaffRole(body.role),
  };
};

export const updatePassword = async (
  password: string,
): Promise<UpdatePasswordRepositoryResult> => {
  const supabase = await createClient();
  const { error } = await supabase.auth.updateUser({
    password,
    data: { password_set: true },
  });

  if (error) {
    return {
      success: false,
      error: error.message,
    };
  }

  return { success: true };
};

export const signOut = async (): Promise<void> => {
  const supabase = await createClient();
  await supabase.auth.signOut();
};

/**
 * Sends a Supabase invite email via the admin API (Cloudflare Pages Function).
 * MUST only be called from an authenticated Admin user (enforced by requireAdmin
 * in api/team.ts and by the server-side admin JWT check on /api/team/invite).
 * Do NOT use signInWithOtp for staff onboarding — magic links bypass invite controls.
 */
export const inviteStaff = async (
  email: string,
  redirectTo: string,
  options?: { resend?: boolean },
): Promise<InviteStaffRepositoryResult> => {
  const supabase = await createClient();
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    return { success: false, error: "Not authenticated." };
  }

  const response = await fetch("/api/team/invite", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      email: email.trim().toLowerCase(),
      redirectTo,
      resend: options?.resend === true,
      origin:
        typeof window !== "undefined" ? window.location.origin : undefined,
    }),
  });

  const body = (await response.json()) as InviteStaffRepositoryResult;

  if (!response.ok || !body.success) {
    return {
      success: false,
      error: body.error ?? "Invite failed.",
    };
  }

  return {
    success: true,
    emailSent: body.emailSent,
    inviteUrl: body.inviteUrl,
  };
};

type SyncTeamMemberAuthRoleResult = {
  success: boolean;
  synced?: boolean;
  error?: string;
};

type CreateTeamMemberWithInviteInput = {
  first_name: string;
  last_name: string;
  email: string;
  role: "ADMIN" | "EDITOR";
  is_active: boolean;
  avatar_url: string | null;
};

type CreateTeamMemberWithInviteResult =
  | {
      success: true;
      data: {
        id: string;
        first_name: string;
        last_name: string;
        email: string;
        role: "ADMIN" | "EDITOR";
        is_active: boolean;
        avatar_url: string | null;
        created_at: string;
        updated_at: string;
      };
      emailSent?: boolean;
      inviteUrl?: string;
    }
  | { success: false; error?: string };

export const createTeamMemberWithInvite = async (
  input: CreateTeamMemberWithInviteInput,
  redirectTo: string,
): Promise<CreateTeamMemberWithInviteResult> => {
  const supabase = await createClient();
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    return { success: false, error: "Not authenticated." };
  }

  const response = await fetch("/api/team/create-member", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      ...input,
      email: input.email.trim().toLowerCase(),
      redirectTo,
      origin:
        typeof window !== "undefined" ? window.location.origin : undefined,
    }),
  });

  const body = (await response.json()) as CreateTeamMemberWithInviteResult;

  if (!response.ok || !("success" in body) || !body.success) {
    const errorBody = body as { error?: string };
    return {
      success: false,
      error: errorBody.error ?? "Create failed.",
    };
  }

  return body;
};

export const syncTeamMemberAuthRole = async (
  email: string,
): Promise<SyncTeamMemberAuthRoleResult> => {
  const supabase = await createClient();
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    return { success: false, error: "Not authenticated." };
  }

  const response = await fetch("/api/team/sync-role", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      email: email.trim().toLowerCase(),
    }),
  });

  const body = (await response.json()) as SyncTeamMemberAuthRoleResult & {
    reason?: string;
  };

  if (!response.ok || !body.success) {
    return {
      success: false,
      error: body.error ?? "Failed to sync auth role.",
    };
  }

  return {
    success: true,
    synced: body.synced !== false,
  };
};
